import { useEffect, useState } from "react";
import { getCourseById } from "../../areas/launchPad/services/courseService";
import CompletionModal from "../../areas/launchPad/components/modal";
import { useSelector } from "react-redux";
import {
  getDomainById,
  getNextCourse,
} from "../../areas/launchPad/services/domainService";
import { getSkillPathDetails } from "../../areas/launchPad/services/skillPathService";
import { DOMAIN_TYPES } from "../../areas/launchPad/constants/domainTypes";
import { MODAL_TYPES } from "../../areas/launchPad/constants/modalTypes";

export const ModalWrapper = () => {
  // const navigate = useNavigate();
  const [modal, setModal] = useState({
    open: false,
  });

  // Arrays of all new completed courses, skillpaths and job domains to show the completion modal for each one
  const pendingCompletedCourses = useSelector((state) =>
    state.launchPadLayout.pendingCompletedCourses.filter((item) => item.pending)
  );
  const pendingCompletedSkillpaths = useSelector((state) =>
    state.launchPadLayout.pendingCompletedSkillpaths.filter(
      (item) => item.pending
    )
  );
  const pendingCompletedJobDomains = useSelector((state) =>
    state.launchPadLayout.pendingCompletedJobDomains.filter(
      (item) => item.pending
    )
  );

  useEffect(() => {
    // If there are pending course completion modals show the first one
    if (pendingCompletedCourses[0]) {
      // Check the first element of the array of the completed courses
      if (pendingCompletedCourses[0]?.pending) {
        const courseId = pendingCompletedCourses[0].courseId;
        const domainTypeId = pendingCompletedCourses[0].domainTypeId;
        const finishedFromSkillPathId =
          pendingCompletedCourses[0].finishedFromSkillPathId;
        const finishedFromDomainId =
          pendingCompletedCourses[0].finishedFromDomainId;
        // Get the course details
        getCourseById(courseId).then((courseResponse) => {
          // If it is an academy skillpath course, get the details of the corresponding skillpath
          // Then check if the skillpath is complete or not to also display the completion modal for the skillpath
          if (domainTypeId === DOMAIN_TYPES.ACADEMY) {
            getSkillPathDetails(finishedFromSkillPathId).then(
              (skillPathResponse) => {
                handleCourseResponse(courseResponse, skillPathResponse, true);
              }
            );
            // If it is a job domain course, get the details of the corresponding job domain
            // Then check if the job domain is complete or not to also display the completion modal for the job domain
          } else {
            getDomainById(finishedFromDomainId).then((domainResponse) => {
              handleCourseResponse(courseResponse, domainResponse, false);
            });
          }
        });
      }
    }
  }, [pendingCompletedCourses.length]);

  // Check if the skillpath or job domain is complete or not and set the modal properties accordingly
  const handleCourseResponse = (
    courseResponse,
    detailsResponse,
    isAcademyCourse
  ) => {
    const isComplete =
      detailsResponse.data.completedCourses ===
      detailsResponse.data.totalCourses;
    const completionData = getCompletionData(detailsResponse, isAcademyCourse);
    if (isComplete) {
      const modalProperties = setModalProperties(
        courseResponse,
        completionData,
        {},
        isAcademyCourse
      );
      setModal(modalProperties);
    } else {
      // If skillpath or job domain is not complete get the next course details
      getNextCourse(
        isAcademyCourse
          ? detailsResponse.data.domainId
          : detailsResponse.data.id
      ).then((nextCourseResponse) => {
        getCourseById(nextCourseResponse.data.id).then(
          (nextCourseResponseDetails) => {
            const nextCourseData = getNextCourseData(
              nextCourseResponseDetails,
              nextCourseResponse
            );
            const modalProperties = setModalProperties(
              courseResponse,
              completionData,
              nextCourseData,
              isAcademyCourse
            );
            setModal(modalProperties);
          }
        );
      });
    }
  };

  // Determines completion data based on whether it is an academy skillpath course or a job domain course
  const getCompletionData = (response, isAcademyCourse) => ({
    completedCourses: response.data.completedCourses,
    totalCourses: response.data.totalCourses,
    // Set the domain id depending on the type of course as they have different response properties
    domainId: isAcademyCourse ? response.data.domainId : response.data.id,
    // If it is an academy skillpath course set the skillpath id
    ...(isAcademyCourse && { skillPathId: response.data.skillPathId }),
  });

  // Sets data for the next course if there is one
  const getNextCourseData = (detailsResponse, response) => {
    return {
      nextCourseId: detailsResponse.data.id,
      nextCourseName: detailsResponse.data.title,
      nextCourseThumbnail: detailsResponse.data.thumbnail,
      nextCourseSkillPathId: response.data.skillPathId,
    };
  };

  // Sets properties for the modal based on the course details and completion data
  const setModalProperties = (
    courseResponse,
    completionData,
    nextCourseData,
    isAcademyCourse
  ) => {
    // Set the general modal properties and then add the completion data and next course data if there is one
    const modalProperties = {
      open: true,
      id: courseResponse.data.id,
      title: courseResponse.data.title,
      isAcademyCourse: isAcademyCourse,
      type: MODAL_TYPES.CONTENT,
      ...completionData,
      ...nextCourseData,
    };
    return modalProperties;
  };

  const populateSkillpathModal = (id) => {
    // Check the array of the completed skillpaths to find the one with the id of the modal to open Skillpath completion modal
    const foundSkillPath = pendingCompletedSkillpaths.find((x) => x.id === id);
    if (foundSkillPath?.pending) {
      getSkillPathDetails(foundSkillPath.id).then((skillpathResponse) => {
        setModal({
          open: true,
          id: skillpathResponse.data.skillPathId,
          title: skillpathResponse.data.skillPath,
          userBadgeId: skillpathResponse.data.userBadgeId,
          domainId: skillpathResponse.data.domainId,
          type: MODAL_TYPES.SKILLPATH,
        });
      });
    } else setModal({ open: false });
  };

  const populateDomainModal = (id) => {
    // Check the array of the completed jobs domains to find the one with the id of the modal to open Job completion modal
    const foundDomain = pendingCompletedJobDomains.find((x) => x.id === id);
    if (foundDomain?.pending) {
      getDomainById(foundDomain.id).then((domainResponse) => {
        setModal({
          open: true,
          id: domainResponse.data.id,
          title: domainResponse.data.name,
          domainId: domainResponse.data.id,
          type: MODAL_TYPES.DOMAIN,
        });
      });
    } else setModal({ open: false });
  };

  return (
    <CompletionModal
      modal={modal}
      populateSkillpathModal={populateSkillpathModal}
      populateDomainModal={populateDomainModal}
      setModal={setModal}
    />
  );
};
